import * as Sentry from '@sentry/react';
import { Auth } from 'aws-amplify';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useMst } from '../../../mobx-models/Root';
import { useConstructor } from '../../../utilities';
import LoginArea from './LoginArea';
import NewPasswordArea from './NewPasswordArea';

enum LoginViews {
  LOGIN,
  NEW_PASSWORD,
}

interface Props {
  loginHandler: (history: any) => Promise<void>;
}

const Login: React.FC<Props> = ({ loginHandler }: Props) => {
  const history = useHistory();
  const [view, setViewType] = useState<LoginViews>(LoginViews.LOGIN);
  const [userData, setUserData] = useState<any>();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState();

  const { user } = useMst();

  const checkForSession = async () => {
    try {
      const userDetails = await Auth.currentAuthenticatedUser({ bypassCache: true });
      await loginHandler(history);

      Sentry.setUser({ email: userDetails.attributes.email });
    } catch (error) {
      setLoginError(error);
      console.log('🚀 ~ file: Login.tsx ~ line 36 ~ checkForSession ~ error', error);
    }
  };

  useConstructor(() => {
    checkForSession();
  });

  const getPermissions = async () => {
    try {
      await user.getProfile();
    } catch (error) {
      setLoginError(error);
      console.log('🚀 ~ file: Login.tsx ~ line 52 ~ getPermissions ~ error', error);
    }
  };

  const handleLogin = async (email: string, password: string) => {
    setIsLoggingIn(true);
    try {
      const lowerCaseEmail = email.toLowerCase();
      const awsUser = await Auth.signIn(lowerCaseEmail, password);

      if (awsUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setUserData(awsUser);
        setViewType(LoginViews.NEW_PASSWORD);
        return;
      }

      // Gets and assigns siteId to User Model
      await getPermissions();

      Sentry.setUser({ email: lowerCaseEmail });
      await loginHandler(history);
    } catch (error) {
      setLoginError(error);
      console.log('error signing in', error);
    }
    setIsLoggingIn(false);
  };

  const saveNewPassword = async (newPassword: string) => {
    try {
      await Auth.completeNewPassword(userData, newPassword);

      history.push('/home');
    } catch (error) {
      console.log('🚀 ~ file: Login.tsx ~ line 85 ~ saveNewPassword ~ error', error);
    }
  };

  const clearError = () => {
    setLoginError(undefined);
  };

  const getView = () => {
    switch (view) {
      case LoginViews.NEW_PASSWORD:
        return <NewPasswordArea saveNewPassword={saveNewPassword} />;
      case LoginViews.LOGIN:
      default:
        return (
          <LoginArea
            loginUser={handleLogin}
            isLoggingIn={isLoggingIn}
            error={loginError}
            clearError={clearError}
          />
        );
    }
  };

  return <>{getView()}</>;
};

export default Login;
